import {AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Output, Renderer2, ViewEncapsulation, DOCUMENT, ChangeDetectionStrategy} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {StateService} from '../../app/services/state.service';
import {ApiService} from '../../app/services/api.service';
import {ActivatedRoute, Router} from '@angular/router';
import {NgxSmartModalService} from 'ngx-smart-modal';
import {TranslateService} from '@ngx-translate/core';
import {CoolLocalStorage} from '@angular-cool/storage';
import {FieldErrorService} from '../../app/services/field-error.service';
import {AnalyticsService} from '../../app/services/analytics.service';
import {DomSanitizer, Title} from '@angular/platform-browser';
import {VisitorService} from '../../app/services/visitor.service';
import {CustomerService} from '../../app/services/customer.service';
import {MatIconRegistry} from '@angular/material/icon';

import moment from 'moment-timezone';
import {InlineWebbookerComponent} from '../inline-webbooker/inline-webbooker.component';
import {QuoteRequestContext, QuoteRequestService} from '../../app/services/quote-request.service';

const apiFormIdProvider = (state: StateService, route: ActivatedRoute) => {
  if (route.parent && route.parent.snapshot) {
    return route.parent.snapshot.params.formId;
  } else {
    return state.formId;
  }
};

interface QuotationConfirmation {
  name: string;
  email: string;
  pickup: string;
  dropoff: string;
  dateTime: string;
}

/**
 * Single-page quotation integration.
 *
 * Merges the wizard's "trip details" (step 1) and "your details" (step 3) parts into one
 * two-column form (see taxiID_Example2_MergedForm.pdf). There is no product/summary/payment
 * step: on submit we reuse the base quotation flow (buildOrder -> saveOrder ->
 * /orders/{id}/requestQuote) and then render an inline confirmation instead of navigating.
 *
 * Extends InlineWebbookerComponent to inherit all form-loading (resolver + Angular Element
 * attribute paths), theming (setColors), and Google/i18n plumbing. Only the template, styles
 * and the submit path differ.
 */
@Component({
    selector: 'app-quotation-widget',
    templateUrl: './inline-quotation.component.html',
    encapsulation: ViewEncapsulation.Emulated,
    styleUrls: [
        './inline-quotation.component.scss',
        '../../app/form/order/order.component.scss'
    ],
    providers: [
        {
            provide: 'apiModel',
            useValue: 'orders'
        },
        {
            provide: 'apiFormId',
            useFactory: apiFormIdProvider,
            deps: [StateService, ActivatedRoute]
        },
        ApiService, CustomerService
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class InlineQuotationComponent extends InlineWebbookerComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  // Distinct formStyle keeps the base formStyle==='widget' branches inert and gives the SCSS a
  // `.wb-formstyle-quotation` scope.
  formStyle = 'quotation';
  navigation = 'inline';
  @Input() origin: string = 'inline-quotation';
  @Input() widgettype: string = 'inline-quotation';
  // Unlike the other embedded widgets, the quotation form defaults to the form's OWN configured
  // palette rather than forcing the generic #597db0/#000/transparent. InlineWebbookerComponent's
  // setColors() writes these @Inputs over styles.colors (e.g. headercolor -> global-title-color,
  // which the button/accent colors reference via $global-title-color), so a non-empty default
  // would mask the form's main colour. Empty means "use the form config"; a host that embeds
  // <inline-quotation headercolor="..."> still overrides it via ngOnChanges.
  @Input() headercolor: string = '';
  @Input() textcolor: string = '';
  @Input() backgroundcolor: string = '';

  submitted = false;
  confirmation: QuotationConfirmation = {name: '', email: '', pickup: '', dropoff: '', dateTime: ''};
  // Return-trip date field, cloned from the outbound date field (built after the form loads).
  returnDateField: any = null;

  // OrderComponent keeps `apiService` private, so hold our own reference to the same
  // component-level instance (DI resolves it once per component) for submitQuotation().
  private quoteApiService: ApiService;
  // Base keeps `_hostEl` private; keep our own reference for the font-inheritance policy.
  private qHostEl: ElementRef;

  constructor(http: HttpClient,
              iconRegistry: MatIconRegistry,
              sanitizer: DomSanitizer,
              activatedRoute: ActivatedRoute,
              apiService: ApiService,
              modalService: NgxSmartModalService,
              translate: TranslateService,
              vault: CoolLocalStorage,
              router: Router,
              route: ActivatedRoute,
              state: StateService,
              errorService: FieldErrorService,
              analytics: AnalyticsService,
              pageTitle: Title,
              visitorService: VisitorService,
              _customerService: CustomerService,
              _renderer2: Renderer2,
              _iconRegistry: MatIconRegistry,
              _sanitizer: DomSanitizer,
              @Inject('Window') window: Window,
              @Inject(DOCUMENT) document: Document,
              _hostEl: ElementRef) {
    super(http, iconRegistry, sanitizer, activatedRoute, apiService, modalService, translate, vault, router, route,
      state, errorService, analytics, pageTitle, visitorService, _customerService, _renderer2, _iconRegistry,
      _sanitizer, window, document, _hostEl);
    this.quoteApiService = apiService;
    this.qHostEl = _hostEl;
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    // Host is now in the DOM and setColors() has run — resolve the font policy.
    setTimeout(() => {
      this.applyInheritedFont();
      this.ensureAddressLabels();
      this.buildReturnDateField();
    }, 0);
  }

  /**
   * Toggle the return-trip requirement (the Yes/No control below the trip fields).
   */
  setReturnTrip(value: boolean): void {
    if (this.formGroup && this.formGroup.controls['returnTrip']) {
      this.formGroup.controls['returnTrip'].setValue(value);
    }
    if (value && !this.returnDateField) {
      this.buildReturnDateField();
    }
  }

  /**
   * Build the return-date field by cloning the outbound date field and pointing it at the
   * return-date control/mode. app-date-time-field binds to config.property and auto-detects
   * the return variant when the property starts with "return".
   */
  private buildReturnDateField(): void {
    const part = this.tripPart;
    if (!part) {
      return;
    }
    const fields = part.fieldsWithLayoutInfo || part.fields || [];
    const dateField = fields.find((f: any) => f.type === 'dateTime');
    if (!dateField) {
      return;
    }
    let mode = 'returnRequestedDate';
    const modeCtrl = this.formGroup && this.formGroup.controls['returnRequestedDateMode'];
    if (modeCtrl && modeCtrl.value && this.formGroup.controls[modeCtrl.value]) {
      mode = modeCtrl.value;
    }
    this.returnDateField = {
      ...dateField,
      property: mode,
      label: 'return_date',
      hidden: false,
      required: true
    };
  }

  /**
   * The pickup/dropoff inputs are rendered by a single `sortable-address` field, whose
   * departure/destination sub-inputs carry no label (field-wrapper suppresses the label for
   * that field type). For the quotation form we want visible "departure"/"destination"
   * labels, so opt those two sub-configs into the inline label the sortable-address component
   * renders. Label text comes from src/assets/default.json (editable) with a safe fallback.
   * Gated by `showInlineLabel` so no other form/integration is affected.
   */
  private ensureAddressLabels(): void {
    let defaults: any = {};
    try {
      // @ts-ignore — JSON asset, same pattern as default_css.json.
      defaults = require('../../assets/default.json') || {};
    } catch (e) { /* fall back to defaults below */ }
    const q = (defaults && defaults.quotation) || {};
    const departureLabel = q.departureLabel || 'departure';
    const destinationLabel = q.destinationLabel || 'destination';

    const parts = (this.form && this.form.config && this.form.config.parts) || [];
    parts.forEach((part: any) => {
      const fields = part.fields || [];
      const hasSortable = (part.fieldsWithLayoutInfo || fields).some((f: any) => f.type === 'sortable-address');
      if (!hasSortable) {
        return;
      }
      // Convention (see field-wrapper): fields[0] = departure, fields[2] = destination.
      if (fields[0]) {
        fields[0].showInlineLabel = true;
        if (!fields[0].label) { fields[0].label = departureLabel; }
      }
      if (fields[2]) {
        fields[2].showInlineLabel = true;
        if (!fields[2].label) { fields[2].label = destinationLabel; }
      }
    });
  }

  setColors(): void {
    super.setColors();
    this.applyInheritedFont();
  }

  /**
   * Font policy for the quotation widget, resolved into the widget-scoped --wbq-font:
   *   1. If the form config specifies an explicit font, use it.
   *   2. Embedded as the <inline-quotation> custom element on a host site: inherit that
   *      site's font (and if it traces to the browser default serif, fall back to Arial).
   *   3. On our direct link (routed <app-quotation-widget>, no surrounding website): load
   *      our default form font from default_css.json instead of inheriting — otherwise the
   *      guaranteed-invalid `inherit` trick would resolve to the browser-default serif.
   */
  private applyInheritedFont(): void {
    try {
      const host = this.qHostEl && this.qHostEl.nativeElement;
      if (!host) {
        return;
      }
      const win = this.document.defaultView || window;
      const configFont = win.getComputedStyle(this.document.documentElement)
        .getPropertyValue('--form-fontstyle').trim();
      // The custom element mounts as <inline-quotation>; the routed direct link mounts the
      // component under its selector <app-quotation-widget>.
      const isEmbedded = (host.tagName || '').toLowerCase() === 'inline-quotation';

      let resolved: string;
      if (configFont && configFont.toLowerCase() !== 'inherit') {
        // The form config specified an explicit font-family (may include an invalid leading
        // "Inherit," token — sanitized below).
        resolved = configFont;
      } else if (isEmbedded) {
        // Inherit the host website's font; fall back to Arial if that is the browser default.
        const parent = host.parentElement || this.document.body;
        const inherited = (parent ? win.getComputedStyle(parent).fontFamily : '') || '';
        const norm = inherited.replace(/["']/g, '').trim().toLowerCase();
        const isBrowserDefault = !norm || norm === 'serif' || norm === 'times new roman' || norm === 'times';
        resolved = isBrowserDefault ? 'Arial, sans-serif' : inherited;
      } else {
        // Direct link: load our default form font.
        let defaultFont = 'Arial, Roboto, sans-serif';
        try {
          // @ts-ignore — JSON asset, same pattern as setColors().
          const css = require('../../assets/default_css.json') || {};
          if (css['form-fontstyle']) {
            // A CSS-wide keyword (e.g. a leading "Inherit,") inside a font-family LIST makes
            // the whole declaration invalid → serif. Strip it so we render the intended stack.
            const cleaned = String(css['form-fontstyle']).replace(/^\s*inherit\s*,\s*/i, '').trim();
            if (cleaned) {
              defaultFont = cleaned;
            }
          }
        } catch (e) { /* keep fallback */ }
        resolved = defaultFont;
      }
      // A CSS-wide keyword such as a leading "Inherit," inside a font-family LIST voids the
      // whole declaration (→ browser-default serif). Strip it so --wbq-font stays valid.
      resolved = String(resolved).replace(/^\s*inherit\s*,\s*/i, '').trim();
      const finalFont = resolved || 'Arial, sans-serif';
      host.style.setProperty('--wbq-font', finalFont);
      // Also set the shared --form-fontstyle on document.documentElement: body-portaled UI
      // that isn't a descendant of `host` — flatpickr's calendar, intl-tel-input's country
      // list — can't see --wbq-font through inheritance, so it needs this instead.
      // Embedded: write the neutral 'inherit' sentinel, mirroring InlineWebbookerComponent's
      // own convention, NOT the resolved value. Two things break if we wrote finalFont here:
      // (1) this method runs again via ngAfterViewInit's setTimeout(0) after setColors()
      // already called it once, and that second call reads `configFont` back from this same
      // property — a concrete value would be misread as "the form config specified this
      // font" and reused verbatim instead of re-deriving from the (possibly since-changed)
      // host font; (2) a page embedding more than one widget would have whichever one
      // resolves last silently override every other widget's body-portaled font. 'inherit'
      // is immune to both: it's excluded from the "explicit font" check above, and writing it
      // twice (by one or several widgets) is idempotent.
      // Direct link: exactly one instance ever exists on that page, so the concrete value is
      // safe, and needed — nothing else sets a font on <body> there for flatpickr to fall
      // back to.
      this.document.documentElement.style.setProperty('--form-fontstyle', isEmbedded ? 'inherit' : finalFont);
    } catch (e) {
      // Non-fatal: fall back to the SCSS default (--form-fontstyle, then Arial).
    }
  }

  /**
   * Left column ("trip details" / step 1): the first non-products/summary/completion part.
   * Mirrors the anchoring logic of the inherited `quoteContactPart` getter.
   */
  get tripPart(): any {
    const parts = (this.form && this.form.config && this.form.config.parts) || [];
    return parts.find((p: any) =>
      p.type !== 'products' && p.type !== 'summary' && p.type !== 'completion' && p.type !== 'address'
    ) || null;
  }

  get tripFields(): any[] {
    const part = this.tripPart;
    if (!part) {
      return [];
    }
    const fields = (part.fieldsWithLayoutInfo || part.fields || []).filter((f: any) => !f.hidden);
    // Edge case: a single-part form where trip and contact share one part. Keep only the
    // trip-shaped fields on the left; the email/phone/name fields go to the right column.
    if (part === this.quoteContactPart) {
      return fields.filter((f: any) => !this.isContactField(f));
    }
    return fields;
  }

  get contactFields(): any[] {
    const part = this.quoteContactPart;
    if (!part) {
      return [];
    }
    const fields = (part.fieldsWithLayoutInfo || part.fields || []).filter((f: any) => !f.hidden);
    if (part === this.tripPart) {
      return fields.filter((f: any) => this.isContactField(f));
    }
    return fields;
  }

  private isContactField(field: any): boolean {
    const contactProps = ['firstName', 'lastName', 'email', 'phoneNumber', 'note'];
    const contactTypes = ['email', 'phone', 'textarea'];
    return contactProps.includes(field.property) || contactTypes.includes(field.type);
  }

  /**
   * Validate both merged parts, then send the quotation and show the inline confirmation.
   * Delegates the buildOrder -> saveOrder -> requestQuote sequence to QuoteRequestService (the
   * same sequence OrderComponent.requestQuote() uses) but ends on `submitted = true` rather than
   * opening the base success modal.
   */
  submitQuotation(): void {
    const self = this;
    const values = this.formGroup.getRawValue();
    // The merged form has no payment step; the return-trip branch of buildOrder() dereferences
    // values.paymentMethod without a guard, so default it to an empty string.
    if (values.paymentMethod == null) {
      values.paymentMethod = '';
    }

    const parts = (this.form && this.form.config && this.form.config.parts) || [];
    const tripIndex = parts.indexOf(this.tripPart);
    const contactIndex = parts.indexOf(this.quoteContactPart);

    let valid = true;
    if (tripIndex > -1) {
      this.checkPageValidity(values, tripIndex);
      valid = valid && this.pageStates[tripIndex] === true;
    }
    if (contactIndex > -1 && contactIndex !== tripIndex) {
      this.checkPageValidity(values, contactIndex);
      valid = valid && this.pageStates[contactIndex] === true;
    }

    // Return-trip date is required when a return trip is requested.
    if (values.returnTrip) {
      const rm = values.returnRequestedDateMode || 'returnRequestedDate';
      if (!values[rm]) {
        valid = false;
        this.showAlert(this.nTranslate.instant('return_date_required'));
      }
    }

    if (!valid) {
      this.analytics.umamiEvent('quote_failed', this.analyticsContext({stage: 'validate', reason: 'invalid_fields'}));
      this.scrollUp();
      return;
    }

    if (!values.email) {
      this.showAlert(this.nTranslate.instant('request_quote_email_required'));
      return;
    }

    this.state.orderPosted = true;
    this.loader = true;
    QuoteRequestService.requestQuote(this.buildInlineQuoteRequestContext(), values).then((result) => {
      self.loader = false;
      if (result.success) {
        self.captureConfirmation(values);
        self.submitted = true;
        self.scrollUp();
      } else if (result.errorMessage) {
        self.showAlert(result.errorMessage);
      }
    });
  }

  // Builds the QuoteRequestContext QuoteRequestService needs from this component. Uses
  // quoteApiService (this component's own reference — OrderComponent.apiService is private,
  // see its class comment) rather than the inherited one. Named distinctly from
  // OrderComponent's own private quoteRequestContext() — TS treats two same-named `private`
  // members anywhere in a class hierarchy as a hard conflict even when unrelated.
  private buildInlineQuoteRequestContext(): QuoteRequestContext {
    return {
      apiService: this.quoteApiService,
      analytics: this.analytics,
      buildOrder: (v) => this.buildOrder(v),
      saveOrder: () => this.saveOrder(),
      saveState: () => this.saveState(),
      getOrderId: () => this.orderId,
      analyticsContext: (extra) => this.analyticsContext(extra),
    };
  }

  private captureConfirmation(values: any): void {
    const dateValue = values[values.requestedDateMode] || values.requestedDate;
    let dateTime = '';
    if (dateValue) {
      try {
        dateTime = moment(dateValue).tz(this.state.timeZone).format('D MMMM YYYY, HH:mm');
      } catch (e) {
        dateTime = `${dateValue}`;
      }
    }
    this.confirmation = {
      name: values.firstName || '',
      email: values.email || '',
      pickup: (values.departureModel && values.departureModel.internationalAlias) || values.departure || '',
      dropoff: (values.destinationModel && values.destinationModel.internationalAlias) || values.destination || '',
      dateTime
    };
  }
}
